import { createSlice } from '@reduxjs/toolkit';
import CryptoJS from "crypto-js";
const secretKey = process.env.REACT_APP_SECRET_KEY || 'bp-dashboard-key'

const defaultFolders = ['inbox', 'sentitems', 'drafts', 'junkemail', 'deleteditems'];

const saved = localStorage.getItem("requestMail");
let decrypted;
if (saved) {
  try {
    const bytes = CryptoJS.AES.decrypt(saved, secretKey);
    decrypted = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (e) {
    localStorage.removeItem("requestMail");
  }
}

// Apply locally-saved read overrides on top of Graph API data
// so that emails the user has read don't revert to unread after refresh.
function applyReadOverrides(emails, overrides) {
  if (!overrides || !Object.keys(overrides).length) return emails;
  return emails.map(e =>
    overrides[e.conversationId] !== undefined
      ? { ...e, isRead: overrides[e.conversationId] }
      : e
  );
}

const initialState = {
  byUser: {},
  mail: null,
  prevmail: null,
  MailList: [],
  openWindow: false,
  requestMail: saved ? decrypted : [],
  flagged:       (() => { try { return JSON.parse(localStorage.getItem('email_flagged')        || '{}'); } catch { return {}; } })(),
  pinned:        (() => { try { return JSON.parse(localStorage.getItem('email_pinned')         || '{}'); } catch { return {}; } })(),
  readOverrides: (() => { try { return JSON.parse(localStorage.getItem('email_read_overrides') || '{}'); } catch { return {}; } })(),
};

const emailSlice = createSlice({
  name: 'emails',
  initialState,
  reducers: {

    setFolderData: (state, action) => {
      const { user, folderData } = action.payload;
      if (!state.byUser[user]) return;

      Object.entries(folderData).forEach(([folder, data]) => {
        state.byUser[user][folder] = {
          ...state.byUser[user][folder],
          data,
        };
      });
    },
    setRequestNotification(state, action) {
      const email = action.payload;

      const existingIndex = state.requestMail.findIndex(
        (item) => item.mail === email
      );

      if (existingIndex !== -1) {
        state.requestMail[existingIndex].index += 1;
      } else {
        state.requestMail.push({
          mail: email,
          index: 1
        });
      }
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(state.requestMail),
        secretKey
      ).toString();
      localStorage.setItem("requestMail", encrypted);
    },

    setremoveNotification(state, action) {
      const mail = action.payload;

      state.requestMail = state.requestMail.filter(
        item => item.mail !== mail
      );

      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(state.requestMail),
        secretKey
      ).toString();
      localStorage.setItem("requestMail", encrypted);
    },

    setAllFolderData: (state, action) => {
      const { user, folders } = action.payload;
      if (!state.byUser[user]) state.byUser[user] = {};
      const overrides = state.readOverrides[user] || {};
      // Apply read overrides so isRead state survives refresh
      const patched = {};
      Object.entries(folders).forEach(([key, fd]) => {
        patched[key] = { ...fd, data: applyReadOverrides(fd.data || [], overrides) };
      });
      // Merge: update only folders returned by Graph, preserve others (e.g. optimistic SES sent items)
      state.byUser[user].folders = { ...(state.byUser[user].folders || {}), ...patched };
    },

    appendToFolder: (state, action) => {
      const { user, folder, newMessages } = action.payload;
      if (!state.byUser[user]) return;

      const folderState = state.byUser[user].folders?.[folder];
      if (!folderState) return;

      folderState.data.push(...newMessages);
      folderState.skip += newMessages.length;
    },
    prependToFolder: (state, action) => {
      const { user, folder, message } = action.payload;
      if (!state.byUser[user]) return;
      if (!state.byUser[user].folders) state.byUser[user].folders = {};
      if (!state.byUser[user].folders[folder]) {
        state.byUser[user].folders[folder] = { data: [], skip: 0, hasMore: true };
      }
      state.byUser[user].folders[folder].data.unshift(message);
    },

    updatePagination: (state, action) => {
      const { user, folder, skip, hasMore } = action.payload;
      if (!state.byUser[user]) return;

      const folderState = state.byUser[user].folders?.[folder];
      if (!folderState) return;

      folderState.skip = skip;
      folderState.hasMore = hasMore;
    },
    moveToDeletedFolder: (state, action) => {
      const { user, folder, conversationId, email } = action.payload;
      if (!state.byUser[user]) return;

      const userFolders = state.byUser[user].folders;
      if (!userFolders?.[folder]) return;

      userFolders[folder].data = userFolders[folder].data.filter(
        (e) => e.conversationId !== conversationId
      );
      // Only add to deleteditems if it's already loaded in Redux state
      if (userFolders?.deleteditems) {
        userFolders.deleteditems.data.unshift({ ...email, conversationId });
      }
    },

    // Permanent delete — removes from source folder without moving to Deleted Items
    removeEmailFromFolder: (state, action) => {
      const { user, folder, conversationId } = action.payload;
      if (!state.byUser[user]?.folders?.[folder]) return;
      state.byUser[user].folders[folder].data = state.byUser[user].folders[folder].data.filter(
        (e) => e.conversationId !== conversationId
      );
    },

    markAsReadInState: (state, action) => {
      const { user, folder, conversationId } = action.payload;
      if (!state.byUser[user]) return;

      const folderData = state.byUser[user].folders?.[folder];
      if (!folderData) return;

      folderData.data = folderData.data.map((email) =>
        email.conversationId === conversationId ? { ...email, isRead: true } : email
      );
      // Persist so refresh doesn't revert to unread
      if (!state.readOverrides[user]) state.readOverrides[user] = {};
      state.readOverrides[user][conversationId] = true;
      localStorage.setItem('email_read_overrides', JSON.stringify(state.readOverrides));
    },

    setEmailReadStatus: (state, action) => {
      const { user, folder, conversationId, isRead } = action.payload;
      if (!state.byUser[user]) return;
      const folderData = state.byUser[user].folders?.[folder];
      if (!folderData) return;
      folderData.data = folderData.data.map((email) =>
        email.conversationId === conversationId ? { ...email, isRead } : email
      );
      // Persist read override
      if (!state.readOverrides[user]) state.readOverrides[user] = {};
      if (isRead) {
        state.readOverrides[user][conversationId] = true;
      } else {
        delete state.readOverrides[user][conversationId];
      }
      localStorage.setItem('email_read_overrides', JSON.stringify(state.readOverrides));
    },

    pushOrReplaceAtTop: (state, action) => {
      const { user, folder, email } = action.payload;

      if (!state.byUser[user]) return;
      if (!state.byUser[user].folders) state.byUser[user].folders = {};
      if (!state.byUser[user].folders[folder]) {
        state.byUser[user].folders[folder] = { data: [], skip: 0, hasMore: true };
      }

      const folderData = state.byUser[user].folders[folder].data;
      const index = folderData.findIndex((e) => e.conversationId === email.conversationId);

      if (index !== -1) {
        folderData.splice(index, 1);
      }
      folderData.unshift(email);
    },

    setCurrentMail(state, action) {
      state.mail = action.payload;
    },

    setCurrentMailId(state, action) {
      state.CurrentMailId = action.payload;
    },
    setMailList(state, action) {
      state.MailList = action.payload;
    },
    setFolderMeta: (state, action) => {
      const { user, folderMeta } = action.payload;
      if (!state.byUser[user]) state.byUser[user] = {};
      state.byUser[user].folderMeta = folderMeta;
      // Persist so folder list survives hard refresh (shown instantly before Graph re-fetches)
      try { localStorage.setItem(`email_folder_meta_${user}`, JSON.stringify(folderMeta)); } catch {}
    },
    initFolderInState: (state, action) => {
      const { user, folder, data } = action.payload;
      if (!state.byUser[user]) state.byUser[user] = {};
      if (!state.byUser[user].folders) state.byUser[user].folders = {};
      const overrides = state.readOverrides[user] || {};
      state.byUser[user].folders[folder] = {
        data: applyReadOverrides(data, overrides),
        skip: data.length,
        hasMore: data.length === 50,
      };
    },
    setOpenWindow(state, action) {
      state.openWindow = action.payload
    },
    toggleFlagEmail(state, action) {
      const { user, conversationId } = action.payload;
      if (!state.flagged[user]) state.flagged[user] = [];
      const idx = state.flagged[user].indexOf(conversationId);
      if (idx !== -1) state.flagged[user].splice(idx, 1);
      else state.flagged[user].push(conversationId);
      localStorage.setItem('email_flagged', JSON.stringify(state.flagged));
    },
    togglePinEmail(state, action) {
      const { user, conversationId } = action.payload;
      if (!state.pinned[user]) state.pinned[user] = [];
      const idx = state.pinned[user].indexOf(conversationId);
      if (idx !== -1) state.pinned[user].splice(idx, 1);
      else state.pinned[user].push(conversationId);
      localStorage.setItem('email_pinned', JSON.stringify(state.pinned));
    },

    // Merge poll results into inbox without wiping paginated emails.
    // Only prepends genuinely new emails; updates isRead on existing ones.
    // Never resets skip/hasMore so "load more" continues working.
    mergeInboxPoll(state, action) {
      const { user, newEmails } = action.payload;
      if (!state.byUser[user]?.folders?.inbox) return;
      const overrides = state.readOverrides[user] || {};
      const existing = state.byUser[user].folders.inbox.data;
      const existingIds = new Set(existing.map(m => m.id));
      // Update isRead for emails already in state (apply read overrides too)
      const updated = existing.map(m => {
        const fresh = newEmails.find(n => n.id === m.id);
        const isRead = overrides[m.conversationId] !== undefined
          ? overrides[m.conversationId]
          : (fresh ? fresh.isRead : m.isRead);
        return { ...m, isRead };
      });
      // Prepend emails that are genuinely new
      const brandNew = newEmails
        .filter(m => !existingIds.has(m.id))
        .map(m => ({
          ...m,
          isRead: overrides[m.conversationId] !== undefined ? overrides[m.conversationId] : m.isRead,
        }));
      state.byUser[user].folders.inbox.data = [...brandNew, ...updated];
    },
  },
});

export const {
  initializeUser,
  setMailList,
  setCurrentMail,
  setCurrentMailId,
  setPrevMail,
  setRequestNotification,
  pushOrReplaceAtTop,
  markAsReadInState,
  setEmailReadStatus,
  setAllFolderData,
  setFolderData,
  setremoveNotification,
  appendToFolder,
  prependToFolder,
  moveToDeletedFolder,
  removeEmailFromFolder,
  updatePagination,
  setFolderMeta,
  initFolderInState,
  setOpenWindow,
  toggleFlagEmail,
  togglePinEmail,
  mergeInboxPoll,
} = emailSlice.actions;

export default emailSlice.reducer;
